<script setup lang="ts">
import lottie, { type AnimationItem } from 'lottie-web';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import type { VueElement } from 'vue';

interface Props {
  animationData: Record<string, unknown>;
  loop?: boolean;
  autoplay?: boolean;
  width?: string;
  height?: string;
  renderer?: 'svg' | 'canvas' | 'html';
  preserveAspectRatio?: string;
  speed?: number;
}

const props = withDefaults(defineProps<Props>(), {
  loop: true,
  autoplay: true,
  width: '100%',
  height: '100%',
  renderer: 'svg',
  preserveAspectRatio: 'xMidYMid meet',
  speed: 1,
});

const emit = defineEmits<{
  (e: 'animation:complete'): void;
  (e: 'animation:loop-complete'): void;
  (e: 'animation:enter-frame', frame: number): void;
  (e: 'animation:segment-start'): void;
  (e: 'animation:loaded'): void;
}>();

const animationRef = ref<VueElement | null>(null);
const animation = ref<AnimationItem | null>(null);

const loadAnimation = () => {
  if (!animationRef.value) return;

  if (animation.value) {
    animation.value.destroy();
  }

  animation.value = lottie.loadAnimation({
    container: animationRef.value as Element,
    renderer: props.renderer,
    loop: props.loop,
    autoplay: props.autoplay,
    animationData: props.animationData,
    rendererSettings: {
      preserveAspectRatio: props.preserveAspectRatio,
    },
  });

  animation.value.setSpeed(props.speed);

  animation.value.addEventListener('complete', () => {
    emit('animation:complete');
  });

  animation.value.addEventListener('loopComplete', () => {
    emit('animation:loop-complete');
  });

  animation.value.addEventListener('enterFrame', () => {
    emit('animation:enter-frame', animation.value?.currentFrame || 0);
  });

  animation.value.addEventListener('segmentStart', () => {
    emit('animation:segment-start');
  });

  animation.value.addEventListener('DOMLoaded', () => {
    emit('animation:loaded');
  });
};

const play = () => {
  animation.value?.play();
};

const pause = () => {
  animation.value?.pause();
};

const stop = () => {
  animation.value?.stop();
};

const setSpeed = (speed: number) => {
  animation.value?.setSpeed(speed);
};

const goToAndPlay = (frame: number, isFrame = true) => {
  animation.value?.goToAndPlay(frame, isFrame);
};

const goToAndStop = (frame: number, isFrame = true) => {
  animation.value?.goToAndStop(frame, isFrame);
};

const setDirection = (direction: 1 | -1) => {
  animation.value?.setDirection(direction);
};

watch(() => props.animationData, loadAnimation);
watch(
  () => props.speed,
  (newSpeed) => {
    setSpeed(newSpeed);
  },
);

onMounted(() => {
  loadAnimation();
});

onUnmounted(() => {
  animation.value?.destroy();
});

defineExpose({
  play,
  pause,
  stop,
  setSpeed,
  goToAndPlay,
  goToAndStop,
  setDirection,
});
</script>

<template>
  <div
    ref="animationRef"
    :style="{
      width: width,
      height: height,
    }"
  />
</template>
